import { useContext, useCallback, useEffect } from 'react';

import { denormalize } from 'normalizr';
import { useSelector, useDispatch } from 'react-redux';

import { CurrentDataContext } from 'src/app/current-data';
import { AppDataContext } from 'src/app/data';
import {
  RecentDataContext,
  RecentDataDispatchContext,
} from 'src/contexts/recent-data';
import { fetchData, FetchAction } from 'src/redux/actions';
import { UPDATE_REPOSITORY_BUILD_STATUSES } from 'src/redux/global/actions/repository-build-status';
import { addStatuses, RepositoryBuildStatus } from 'src/redux/global/utils';
import { fetchRecentlyViewedRepositories } from 'src/redux/recently-viewed-repositories';
import { GET_RECENTLY_VIEWED_WORKSPACES } from 'src/redux/workspaces/actions';
import { repository as repoSchema } from 'src/sections/repository/schemas';
import { getRecentlyViewedRepositories } from 'src/selectors/repository-selectors';
import { urls } from 'src/urls/dashboard';
import { authRequest } from 'src/utils/fetch';
import { getRecentProjects } from 'src/utils/recently-viewed-projects-store';
import { recentlyViewedRepositoriesStore as repositoryWithWorkspaceStore } from 'src/utils/recently-viewed-repositories-store';
import { recentlyViewedWorkspacesStore as workspaceStore } from 'src/utils/recently-viewed-workspaces-store';

type RecentData = {
  recentRepositories: BB.Repository[];
  recentProjects: BB.Project[];

  loadRecentRepositories: (
    includeBuildStatus: boolean
  ) => Promise<BB.Repository[]>;
  loadRecentWorkspaces: () => void;
  loadRecentProjects: () => Promise<BB.Project[]>;

  isProjectsLoading: boolean;
  isRepositoriesLoading: boolean;
};

const useRepositoryLoader = () => {
  const appData = useContext(AppDataContext);
  const dispatch = useDispatch();
  const recentContextDispatch = useContext(RecentDataDispatchContext);
  const currentData = useContext(CurrentDataContext);

  const reduxRecents = useSelector(getRecentlyViewedRepositories);
  const { workspace } = currentData;

  useEffect(() => {
    recentContextDispatch({
      type: 'SET_REPOSITORIES',
      payload: reduxRecents,
    });
  }, [recentContextDispatch, reduxRecents]);

  return useCallback(
    async (includeBuildStatus: boolean) => {
      if (!appData.user) {
        return [];
      }

      if (!workspace || !workspace.uuid) {
        return [];
      }

      // storage key expects UUID with braces
      const recentlyViewedRepositories = repositoryWithWorkspaceStore.get({
        uuid: `{${appData.user.uuid}}`,
      });
      const { workspaceUuids } = recentlyViewedRepositories;
      const repositoryUuids = workspaceUuids[workspace.uuid];
      if (
        Object.keys(workspaceUuids).length === 0 ||
        !repositoryUuids ||
        !repositoryUuids.length
      ) {
        return [];
      }

      const uniqueUuids = repositoryUuids.filter(
        (uuid, i, arr) => arr.indexOf(uuid) === i
      );

      const fetchAsyncData = (action: FetchAction): Promise<FetchAction> => {
        // Ignore the `fetch-action-saga` so we can await the fetchData action
        action.meta.isRouterResource = true;
        dispatch(action);
        // @ts-ignore
        return dispatch(fetchData(action));
      };

      const fetchAction = fetchRecentlyViewedRepositories({
        repositoryUuids: uniqueUuids,
        // API request can be made with workspace slug or uuid
        workspaceSlug: workspace.slug || workspace.uuid,
      });
      recentContextDispatch({
        type: 'SET_REPOS_LOADING',
        payload: true,
      });
      const recentReposResponse = await fetchAsyncData(fetchAction);
      const {
        payload: {
          result: { values: responseUuids },
          entities,
        },
      } = recentReposResponse;

      let repos = denormalize(responseUuids, [repoSchema], entities);
      if (!includeBuildStatus) {
        recentContextDispatch({
          type: 'SET_REPOS_LOADING',
          payload: false,
        });
        // We're using implicit knowledge that if we aren't requesting build
        // statuses, this is called from the horizontal nav search. In that
        // case, we're taking some shortcuts to return some (possibly
        // incomplete) data faster. We need to return the repository objects,
        // so denormalize the response.
        return repos;
      }

      try {
        const url = urls.api.internal.repositoryBuildStatuses();
        const response = await fetch(
          authRequest(url, {
            method: 'POST',
            body: `workspace_uuid=${workspace.uuid}&${uniqueUuids
              .map(uuid => `uuid=${uuid}`)
              .join('&')}`,
            headers: {
              'content-type':
                'application/x-www-form-urlencoded; charset=UTF-8',
            },
          })
        );
        recentContextDispatch({
          type: 'SET_REPOS_LOADING',
          payload: false,
        });

        if (response.ok) {
          const statuses: RepositoryBuildStatus[] = await response.json();

          repos = addStatuses(repos, statuses);

          // normalizr will handle this action
          dispatch({
            type: UPDATE_REPOSITORY_BUILD_STATUSES.SUCCESS,
            payload: repos,
            meta: {
              schema: [repoSchema],
            },
          });
        }
      } catch (e) {
        recentContextDispatch({
          type: 'SET_REPOS_LOADING',
          payload: false,
        });
        dispatch({
          type: UPDATE_REPOSITORY_BUILD_STATUSES.ERROR,
          payload: e,
        });
      }

      return repos;
    },
    [dispatch, appData.user, workspace, recentContextDispatch]
  );
};

const useProjectLoader = () => {
  const appData = useContext(AppDataContext);
  const dispatch = useContext(RecentDataDispatchContext);
  const currentData = useContext(CurrentDataContext);

  return useCallback(async () => {
    const recentProjects = getRecentProjects({
      user: appData.user,
      workspace: currentData.workspace?.uuid
        ? { uuid: currentData.workspace?.uuid }
        : undefined,
    });
    const projects: BB.Project[] = [];
    if (
      !appData.user ||
      !currentData.workspace?.uuid ||
      recentProjects.length === 0
    ) {
      return projects;
    }
    const q = encodeURIComponent(
      recentProjects.map(k => `key="${k}"`).join(' OR ')
    );
    try {
      dispatch({
        type: 'SET_PROJECTS_LOADING',
        payload: true,
      });
      const url = `/!api/2.0/workspaces/${currentData.workspace.uuid}/projects/?q=${q}`;
      const response = await fetch(authRequest(url, { method: 'GET' }));
      if (response.ok) {
        // We don't need to update the store with this data, so just
        // read the JSON directly
        // eslint-disable-next-line no-await-in-loop
        const { values: responseProjects } = await response.json();
        projects.push(...responseProjects);
        // Sort the projects based on the order in recentProjects
        projects.sort((a, b) => {
          const aIndex = recentProjects.indexOf(a.key);
          const bIndex = recentProjects.indexOf(b.key);
          return aIndex - bIndex;
        });

        dispatch({
          type: 'SET_PROJECTS',
          payload: projects,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching recent project', e);
    } finally {
      dispatch({
        type: 'SET_PROJECTS_LOADING',
        payload: false,
      });
    }

    return projects;
  }, [appData.user, currentData.workspace?.uuid, dispatch]);
};

const useWorkspacesLoader = () => {
  const appData = useContext(AppDataContext);
  const dispatch = useDispatch();

  return useCallback(() => {
    if (!appData.user) {
      return;
    }

    // storage key expects UUID with braces
    const recentWorkspaces = workspaceStore.get({
      uuid: `{${appData.user.uuid}}`,
    });
    if (recentWorkspaces) {
      dispatch({
        type: GET_RECENTLY_VIEWED_WORKSPACES,
        payload: recentWorkspaces,
      });
    }
  }, [dispatch, appData.user]);
};

export const useRecentData = (): RecentData => {
  const {
    recentProjects,
    recentRepositories,
    isProjectsLoading,
    isRepositoriesLoading,
  } = useContext(RecentDataContext);
  const loadRecentRepositories = useRepositoryLoader();
  const loadRecentProjects = useProjectLoader();
  const loadRecentWorkspaces = useWorkspacesLoader();

  return {
    recentRepositories,
    recentProjects,
    loadRecentRepositories,
    loadRecentWorkspaces,
    loadRecentProjects,
    isProjectsLoading,
    isRepositoriesLoading,
  };
};
